/**
 * @fileoverview Switch/toggle input component for binary on/off states
 * @module ctl-switch
 * @version 1.0.0
 * 
 * @description
 * A styled switch (toggle) component that provides an alternative to checkboxes for
 * binary on/off states. Switches are best used for settings that take effect immediately,
 * while checkboxes are better for options that require form submission.
 * 
 * The component features a smooth sliding animation, supports label text and optional
 * description text, provides full keyboard accessibility, and works with standard form
 * behaviors including disabled state, required validation, and form submission.
 * 
 * @example
 * <!-- Basic switch -->
 * <ctl-switch>Enable notifications</ctl-switch>
 * 
 * @example
 * <!-- Checked switch with name/value for forms -->
 * <ctl-switch name="newsletter" value="yes" checked>
 *   Subscribe to newsletter
 * </ctl-switch>
 * 
 * @example
 * <!-- Switch with description -->
 * <ctl-switch name="dark-mode">
 *   Dark Mode
 *   <span slot="description">Use dark theme for better visibility at night</span>
 * </ctl-switch>
 * 
 * @example
 * <!-- Disabled switch -->
 * <ctl-switch disabled checked>
 *   Feature enabled (locked)
 * </ctl-switch>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-3);
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    cursor: pointer;
    user-select: none;
  }

  :host([disabled]) {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .switch-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    pointer-events: none;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: var(--ctl-switch-width);
    height: var(--ctl-switch-height);
    border-radius: calc(var(--ctl-switch-height) / 2);
    background: var(--ctl-color-border);
    transition: background var(--ctl-transition-fast);
    box-sizing: border-box;
  }

  :host(:not([disabled])) .switch-wrapper:hover .switch {
    background: color-mix(in srgb, var(--ctl-color-border) 85%, var(--ctl-color-text));
  }

  input[type="checkbox"]:focus-visible ~ .switch {
    outline: var(--ctl-border-width-thick) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  input[type="checkbox"]:checked ~ .switch {
    background: var(--ctl-color-focus);
  }

  :host(:not([disabled])) input[type="checkbox"]:checked ~ .switch:hover {
    background: color-mix(in srgb, var(--ctl-color-focus) 85%, var(--ctl-color-black));
  }

  .switch-thumb {
    position: absolute;
    top: var(--ctl-switch-padding);
    left: var(--ctl-switch-padding);
    width: var(--ctl-switch-thumb-size);
    height: var(--ctl-switch-thumb-size);
    border-radius: 50%;
    background: var(--ctl-color-white);
    box-shadow: var(--ctl-shadow-sm);
    transition: transform var(--ctl-transition-fast);
  }

  input[type="checkbox"]:checked ~ .switch .switch-thumb {
    transform: translateX(calc(var(--ctl-switch-width) - var(--ctl-switch-height)));
  }

  :host([disabled]) .switch {
    background: color-mix(in srgb, var(--ctl-color-black) 10%, transparent);
    cursor: not-allowed;
  }

  :host([invalid]) .switch {
    background: var(--ctl-color-danger);
  }

  :host([invalid]) input[type="checkbox"]:checked ~ .switch {
    background: var(--ctl-color-danger);
  }

  label {
    cursor: pointer;
    line-height: 1.4;
    font-size: var(--ctl-font-size-base);
  }

  :host([disabled]) label {
    cursor: not-allowed;
  }

  .label-container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
  }

  .description {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    line-height: 1.3;
  }
</style>

<div class="switch-wrapper">
  <input
    type="checkbox"
    id="input"
    part="input"
  />
  <span class="switch" part="switch">
    <span class="switch-thumb"></span>
  </span>
</div>
<div class="label-container">
  <label for="input" part="label">
    <slot></slot>
  </label>
  <div class="description" part="description">
    <slot name="description"></slot>
  </div>
</div>
`;

/**
 * Switch (Toggle) Web Component
 * 
 * A styled switch component for binary on/off states with smooth animation.
 * Best used for settings that take effect immediately.
 * 
 * @class CtlSwitch
 * @extends HTMLElement
 * 
 * @attr {boolean} checked - Whether switch is in on/checked state
 * @attr {boolean} disabled - Whether switch is disabled
 * @attr {boolean} required - Whether switch is required
 * @attr {boolean} invalid - Whether switch is in invalid state
 * @attr {string} name - Form field name for submission
 * @attr {string} value - Value submitted when checked (default: "on")
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot - Default slot for switch label text
 * @slot description - Optional descriptive text shown below the label
 * 
 * @fires ctl-change - Custom event with detail.checked property
 * 
 * @csspart input - The native checkbox input element
 * @csspart switch - The switch track element
 * @csspart label - The label element
 * @csspart description - The description text element
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted text color for description (default: #6b7280)
 * @cssprop --ctl-color-border - Border/track color when off (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Focus ring and checked state color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Error/invalid color (default: #dc2626)
 * @cssprop --ctl-switch-width - Width of switch track (default: 44px)
 * @cssprop --ctl-switch-height - Height of switch track (default: 24px)
 * @cssprop --ctl-switch-padding - Padding around thumb (default: 2px)
 */
export class CtlSwitch extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "checked",
      "disabled",
      "required",
      "invalid",
      "name",
      "value",
      "aria-label",
      "aria-labelledby",
      "aria-describedby",
      "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._input = this.shadowRoot.getElementById("input");
    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._upgradeProperty("checked");
    this._upgradeProperty("disabled");
    this._upgradeProperty("value");
    this._upgradeProperty("name");

    this._input.addEventListener("change", this._onChange);
    this.addEventListener("click", this._onClick);
    this._syncToInput();
  }

  disconnectedCallback() {
    this._input.removeEventListener("change", this._onChange);
    this.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._syncToInput(name);
  }

  // --- Properties ---
  get checked() {
    return this.hasAttribute("checked");
  }

  set checked(value) {
    if (value) {
      this.setAttribute("checked", "");
    } else {
      this.removeAttribute("checked");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }

  set required(value) {
    if (value) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get value() {
    return this.getAttribute("value") || "on";
  }

  set value(val) {
    if (val == null) this.removeAttribute("value");
    else this.setAttribute("value", String(val));
  }

  get name() {
    return this.getAttribute("name");
  }

  set name(val) {
    if (val == null) this.removeAttribute("name");
    else this.setAttribute("name", String(val));
  }

  focus(options) {
    this._input.focus(options);
  }

  blur() {
    this._input.blur();
  }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _syncToInput(changedName) {
    if (!changedName || changedName === "checked") {
      this._input.checked = this.hasAttribute("checked");
    }

    if (!changedName || changedName === "disabled") {
      this._input.disabled = this.hasAttribute("disabled");
    }

    if (!changedName || changedName === "required") {
      this._input.required = this.hasAttribute("required");
    }

    if (!changedName || changedName === "name") {
      const name = this.getAttribute("name");
      if (name) this._input.setAttribute("name", name);
      else this._input.removeAttribute("name");
    }

    if (!changedName || changedName === "value") {
      const value = this.getAttribute("value");
      if (value) this._input.setAttribute("value", value);
      else this._input.removeAttribute("value");
    }

    // ARIA attributes
    const ariaAttrs = ["aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"];
    for (const attr of ariaAttrs) {
      if (!changedName || changedName === attr) {
        if (this.hasAttribute(attr)) {
          this._input.setAttribute(attr, this.getAttribute(attr));
        } else {
          this._input.removeAttribute(attr);
        }
      }
    }

    // Sync aria-invalid based on invalid attribute if not explicitly set
    if (!changedName || changedName === "invalid") {
      if (!this.hasAttribute("aria-invalid")) {
        if (this.hasAttribute("invalid")) {
          this._input.setAttribute("aria-invalid", "true");
        } else {
          this._input.removeAttribute("aria-invalid");
        }
      }
    }
  }

  _onClick(e) {
    // Handle click on host element
    if (this.hasAttribute("disabled")) {
      e.preventDefault();
      return;
    }

    // If click target is not the input itself (e.g., label or host),
    // toggle the switch programmatically
    if (e.target !== this._input) {
      e.preventDefault();
      this.toggle();
    }
  }

  _onChange(e) {
    // Sync checked state from native input to component
    if (this._input.checked) {
      this.setAttribute("checked", "");
    } else {
      this.removeAttribute("checked");
    }

    // Emit custom event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      checked: this._input.checked,
      originalEvent: e
    });
  }

  toggle() {
    if (this.hasAttribute("disabled")) return;
    this.checked = !this.checked;

    // Trigger change event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      checked: this.checked,
      originalEvent: null
    });
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-switch", CtlSwitch);
